import { useState } from "react";
import { Tooltip } from "react-tooltip";
import toast from "react-hot-toast";
import { HiClipboardDocument, HiHandThumbDown, HiHandThumbUp, HiPencilSquare } from "react-icons/hi2";
import EditModal from "./EditModal";
import { classificateMessage, sendEmail } from "../../../api/conversations";
import { Button } from "flowbite-react";
import moment from "moment";

const Message = ({ msg, lookalike, index, conversationId, fetchInfluencer, campaign }) => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loadingEmbed, setLoadingEmbed] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const email = lookalike.contacts.find(item => item.type === 'email')?.value;

  const formatMessage = message => {
    return message.split(/\n+/).map(line => `<p>${line.trim()}</p>`).join('');
  }

  const copyMessage = () => {
    if (navigator.clipboard && msg?.content) {
      navigator.clipboard.writeText(msg.content)
        .then(() => toast.success('Copied successfully'))
        .catch(err => toast.error('Failed to copy'));
    }
  }

  const handleEmbed = async (type) => {
    setLoadingEmbed(true);

    try {
      await classificateMessage({
        id: conversationId,
        data: {
          type,
          index,
        }
      });

      toast.success(type === 'good' ? 'Message upvoted' : 'Message downvoted');
    } catch (error) {
      console.error(error);
      toast.error('Failed to classificate the message');
    } finally {
      setLoadingEmbed(false);
    }
  }

  const handleSendEmail = async () => {
    setIsSendingEmail(true);

    try {
      const response = await sendEmail({
        id: conversationId,
        data: {
          index,
        }
      });

      (response[0]?.errorCode && response[0]?.message) ? toast.error(response[0].message) : toast.success('Email has been sent successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to send an email');
    } finally {
      setIsSendingEmail(false);
    }
  }

  return (
    <div className="p-6 bg-white dark:bg-gray-800 shadow-sm rounded-lg flex items-start gap-6 group relative pe-14">
      <img
        className="size-8 rounded-full shadow-lg"
        src={msg.role === 'human' ? lookalike.profile.picture : '/gradient-ai-robot.jpg'}
        alt={msg.role === 'human' ? lookalike.profile.fullname : 'AI'}
      />
      <div className="text-gray-600">
        {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
        <div className="format" dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
        <div className="mt-4 text-gray-400 text-sm">{moment(msg?.createdAt).format('YYYY-MM-DD HH:mm')}</div>
        {msg.role === 'ai' && (
          <div className="mt-4 text-gray-400 text-sm">Posted by {msg.userId?.username ? msg.userId.username : 'Agent'}</div>
        )}
        {msg.role === 'ai' && (
          <>
            <div className="space-x-2 flex items-center mt-4">
              <button
                type="button"
                className="inline-flex cursor-pointer justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
                data-tooltip-id="copy"
                data-tooltip-content="Copy text"
                onClick={copyMessage}
              >
                <HiClipboardDocument className="size-5" />
              </button>
              <Tooltip
                id="copy"
                style={{
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              />
              <button
                type="button"
                className="inline-flex cursor-pointer justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
                data-tooltip-id="like"
                data-tooltip-content="Like"
                onClick={() => handleEmbed('good')}
                disabled={loadingEmbed}
              >
                <HiHandThumbUp className="size-5" />
              </button>
              <Tooltip
                id="like"
                style={{
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              />
              <button
                type="button"
                className="inline-flex cursor-pointer justify-center rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
                data-tooltip-id="unlike"
                data-tooltip-content="Unike"
                onClick={() => handleEmbed('bad')}
                disabled={loadingEmbed}
              >
                <HiHandThumbDown className="size-5" />
              </button>
              <Tooltip
                id="unlike"
                style={{
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              />
            </div>
            {campaign?.flowType === 'manual' && (
              <div className="mt-4">
                <div
                  data-tooltip-id="send-disabled"
                  data-tooltip-content={!email ? 'Email address was not found for this profile' : null}
                  className="inline-flex"
                >
                  <Button
                    isProcessing={isSendingEmail}
                    disabled={isSendingEmail || !email}
                    onClick={handleSendEmail}
                  >
                    Send email
                  </Button>
                </div>
                <Tooltip
                  id="send-disabled"
                  style={{
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
      {msg.role === 'ai' && (
        <>
          <button
            type="button"
            className="opacity-0 absolute top-4 right-4 group-hover:opacity-100 transition-opacity rounded-lg p-1.5 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
            data-tooltip-id="edit"
            data-tooltip-content="Edit message"
            onClick={() => setOpenEditModal(true)}
          >
            <HiPencilSquare className="size-5" />
          </button>
          <Tooltip
            id="edit"
            style={{
              maxWidth: '250px',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
          />
          <EditModal
            openModal={openEditModal}
            setOpenModal={setOpenEditModal}
            text={msg.content}
            messageId={msg._id}
            index={index}
            conversationId={conversationId}
            fetchInfluencer={fetchInfluencer}
          />
        </>
      )}
    </div>
  );
}

export default Message;
